import { Provider, ProviderSearchFilters } from '../types/provider.types';
import axios, { AxiosInstance } from 'axios';

import { getDataEnvironment } from '../dataEnvironment';

class ApiService {
  private apiClient: AxiosInstance;

  constructor() {
    // In production, use relative URLs (same origin)
    // In development, proxy handles forwarding to backend
    const baseURL = window.location.origin.includes('localhost')
      ? '' // Proxy will handle it
      : ''; // Same origin in production

    this.apiClient = axios.create({
      baseURL,
      timeout: 30000,
      withCredentials: true, // Important for session cookies
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.apiClient.interceptors.request.use((config) => {
      config.headers.set('x-data-environment', getDataEnvironment());
      return config;
    });

    // Add response interceptor to handle 401 errors
    this.apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // Save current location to return after login
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          // Redirect to login with return URL
          window.location.href = `/auth/login?returnUrl=${returnUrl}`;
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Search for providers
   */
  async searchProviders(filters: ProviderSearchFilters, perPage?: number, pageNumber?: number): Promise<any> {
    try {
      const params: any = {};

      console.log('🌐 [ApiService] searchProviders called with:', { filters, perPage, pageNumber });

      // Determine search type based on which field has a value
      if (filters.npi) {
        // NPI search uses vector=npi
        params.keyword = filters.npi;
        params.vector = 'npi';
      } else if (filters.keyword) {
        // Name search uses vector=provider
        params.keyword = filters.keyword;
        params.vector = 'provider';
      }

      // Legacy filters (kept for backward compatibility)
      if (filters.specialty) params.specialty = filters.specialty;
      if (filters.location) params.location = filters.location;
      if (filters.acceptingNewPatients !== undefined) {
        params.acceptingNewPatients = filters.acceptingNewPatients;
      }
      if (filters.gender) params.gender = filters.gender;
      if (filters.language) params.language = filters.language;

      // Pagination parameters
      if (perPage) params.perPage = perPage;
      if (pageNumber) params.pageNumber = pageNumber;

      console.log('🌐 [ApiService] Making GET request to /api/providers/search with params:', params);

      const response = await this.apiClient.get('/api/providers/search', { params });

      console.log('🌐 [ApiService] Response received:', {
        status: response.status,
        dataKeys: Object.keys(response.data || {}),
        totalProviders: response.data?.totalProviders,
        providerResultsLength: response.data?.providerResults?.length
      });

      return response.data;
    } catch (error: any) {
      console.error('❌ [ApiService] Error in searchProviders:', error);
      throw error;
    }
  }

  /**
   * Get provider detail by NPI
   */
  async getProviderDetail(npi: string, signal?: AbortSignal): Promise<Provider> {
    try {
      const response = await this.apiClient.get(`/api/providers/${npi}`, { signal });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Validate a provider against Core API
   * @param npi - Provider NPI
   * @param epicId - Epic Provider ID
   * @param periodOffset - Period offset (0-5) representing 30-day periods (0=next 30 days, 5=150-180 days out)
   */
  async validateProvider(npi: string, epicId: string, periodOffset: number = 0, signal?: AbortSignal): Promise<any> {
    try {
      const response = await this.apiClient.post(`/api/providers/${npi}/validate`, {
        epicId,
        periodOffset
      }, { signal });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Lookup Epic ID directly (no NPI required)
   * @param epicId - Epic Provider ID
   * @param startDate - Start date in YYYY-MM-DD format
   * @param endDate - End date in YYYY-MM-DD format
   */
  async lookupEpicId(epicId: string, startDate: string, endDate: string, signal?: AbortSignal): Promise<any> {
    try {
      const response = await this.apiClient.post('/api/epic/lookup', {
        epicId,
        startDate,
        endDate
      }, { signal });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Validate visit type IDs against phynd.VisitTypeMappings table
   * @param visitTypeIds - Array of visit type IDs to validate
   */
  async validateVisitTypes(visitTypeIds: string[], signal?: AbortSignal): Promise<any> {
    try {
      const response = await this.apiClient.post('/api/providers/validate-visit-types', {
        visitTypeIds
      }, { signal });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Validate visit type IDs against phynd.VisitTypeReason table
   * @param visitTypeIds - Array of visit type IDs to validate
   */
  async validateVisitTypeReasons(visitTypeIds: string[], signal?: AbortSignal): Promise<any> {
    try {
      const response = await this.apiClient.post('/api/providers/validate-visit-type-reasons', {
        visitTypeIds
      }, { signal });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get Epic extract data from LocationDepartmentStaging for a provider
   * @param npi - Provider NPI
   */
  async getEpicExtractData(npi: string, signal?: AbortSignal): Promise<any> {
    try {
      const response = await this.apiClient.get(`/api/providers/${npi}/epic-extract`, { signal });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Latest Epic Open Scheduling block snapshot for a provider. Returns null when
   * the provider is absent from the most recent diagnostic.
   */
  async getEpicBlockSnapshot(npi: string, signal?: AbortSignal): Promise<any | null> {
    try {
      const response = await this.apiClient.get(`/api/epic-blocks/providers/${npi}`, { signal });
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  }

  /**
   * Re-evaluates one provider against Epic's scheduling rules, live. Rule-only,
   * so it returns in about a second; the result is not stored, so the snapshot
   * and the dashboard are unaffected.
   */
  async recheckEpicBlocks(npi: string, signal?: AbortSignal): Promise<any> {
    const response = await this.apiClient.post(`/api/epic-blocks/providers/${npi}/recheck`, {}, { signal });
    return response.data;
  }

  /**
   * Get visit type and location for a provider (Direct Scheduling)
   * @param params - Visit type parameters
   */
  async getVisitTypeAndLocationForProvider(params: {
    loginId: string;
    patientId: string;
    patientIdType: string;
    scheduleType: string;
    providerId: string;
    providerIdType: string;
  }): Promise<any> {
    try {
      const response = await this.apiClient.get('/api/scheduling/visit-type-location', { params });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Get provider by NPI (alias for getProviderDetail)
   * @param npi - Provider NPI
   */
  async getProviderByNPI(npi: string): Promise<Provider> {
    return this.getProviderDetail(npi);
  }
}

const apiService = new ApiService();
export default apiService;
